/**
 * The login door: `POST /v1/auth/otp/request` and `POST /v1/auth/otp/verify`.
 *
 * Both are called with `{ anonymous: true }`. They are the only two routes in the app
 * reached before a session exists, and sending a stale `Authorization` header on them
 * — the one this browser held before it signed out, say — is how a request that should
 * be anonymous gets attributed to somebody.
 *
 * The bodies are `strictObject` on the Worker (`apps/api/src/auth/index.ts`), so an
 * extra field is REFUSED rather than ignored. Every optional field below is therefore
 * omitted rather than sent as `null`: `turnstile_token: null` is a body refusal, not a
 * request without a challenge.
 *
 * Nothing in this file writes a user-visible sentence. `outcome: 'deferred'` carries
 * the Worker's own `notice.message_bn`; every refusal arrives as an `ApiError` whose
 * `message_bn` was written and reviewed on the server (docs/05 § conventions).
 *
 * W2-12 added the rest of the door the screens stand in front of: the channel choice
 * (`GET /v1/auth/channels`), Telegram's first-contact link (`POST /v1/auth/telegram/
 * start-link`), and a name for each of the envelope-less answers those routes give — a
 * throttle and a closed Telegram door — so a screen reads an outcome, not a status code.
 */
import { api, UnmappedApiError } from '../api/client.js';
import { adoptSession, deviceId, type SessionTokens } from './session.js';

/**
 * The country the phone screen is drawn for.
 *
 * A3 draws the country box as a fixed `+৮৮০` with no picker, so the screen can only
 * mean one country and this constant is that fact rather than a policy. REPORTED:
 * docs/11 § country config is what decides which countries are live, and a second
 * copy of that decision living in a client is exactly the drift docs/12 warns about —
 * when the box becomes a picker, this constant should come from the server.
 */
export const DEFAULT_DIALLING_CODE = '+880';

const BANGLA_ZERO = 0x09e6;

/** `০১৭১২` → `01712`. Not copy: a numeral is the same number in either script. */
export function toAsciiDigits(input: string): string {
  let out = '';
  for (const ch of input) {
    const code = ch.codePointAt(0) ?? 0;
    out += code >= BANGLA_ZERO && code <= BANGLA_ZERO + 9 ? String(code - BANGLA_ZERO) : ch;
  }
  return out;
}

const ASCII_ZERO = 0x30;

/** `01712` → `০১৭১২`, for a Bangla sentence that has to name the user's own number. */
export function toBanglaDigits(input: string): string {
  let out = '';
  for (const ch of input) {
    const code = ch.codePointAt(0) ?? 0;
    out +=
      code >= ASCII_ZERO && code <= ASCII_ZERO + 9
        ? String.fromCodePoint(BANGLA_ZERO + (code - ASCII_ZERO))
        : ch;
  }
  return out;
}

/**
 * What the person typed, as the E.164 the Worker's schema demands, or `null`.
 *
 * `null` means "this cannot be dialled", and the caller does NOT send it. That is not
 * the client deciding a refusal on the server's behalf: a body the schema will refuse
 * spends the caller's per-IP budget and a challenge token to learn something already
 * known here, and the refusal it earns is an `E_PERMISSION` with an empty `message_bn`
 * that no drawn state on this surface can render. See `failure.tsx`.
 */
export function toE164(raw: string, diallingCode = DEFAULT_DIALLING_CODE): string | null {
  const ascii = toAsciiDigits(raw).trim();
  const plus = ascii.startsWith('+') || ascii.startsWith('00');
  const digits = ascii.replace(/\D/g, '');
  if (digits === '') return null;
  // Already international: the person typed the country in, so it is not added twice.
  const e164 = plus
    ? `+${digits.replace(/^0+/, '')}`
    : `${diallingCode}${digits.replace(/^0+/, '')}`;
  // The Worker's own shape, applied here only to decide whether to send at all.
  return /^\+[1-9]\d{6,14}$/.test(e164) ? e164 : null;
}

/**
 * Is this plausibly a number a code can reach?
 *
 * `toE164` answers "can this be dialled at all", which is every country's question. For
 * the one country A3 draws (`+৮৮০` in a fixed box), a mobile number has a shape: a
 * national `01` followed by an operator digit from ৩ to ৯ and eight more digits. A
 * landline or a digit short of that is refused HERE, with the field's own error, rather
 * than spending one of the five codes an hour this number is allowed on a number no
 * WhatsApp or Telegram account can have.
 *
 * A number typed with another country code is left to the Worker: the client knows the
 * shape of one country's mobile numbers, and pretending to know the others would be a
 * second, silent copy of docs/11 § country config.
 */
export function isPlausibleMobile(e164: string): boolean {
  if (!e164.startsWith(DEFAULT_DIALLING_CODE)) return /^\+[1-9]\d{6,14}$/.test(e164);
  return /^\+8801[3-9]\d{8}$/.test(e164);
}

/* ═════════════════════════════════════════════════════ the two channels ══ */

/** The two transports a person may ask for. The Worker decides which one carries it. */
export type OtpChannel = 'whatsapp' | 'telegram';

/** One row of `GET /v1/auth/channels`. The Bangla is the Worker's (`auth/bodies.ts`). */
export interface ChannelOption {
  key: OtpChannel;
  label_bn: string;
  hint_bn?: string | undefined;
}

function isChannelOption(value: unknown): value is ChannelOption {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as { key?: unknown; label_bn?: unknown; hint_bn?: unknown };
  return (
    (row.key === 'whatsapp' || row.key === 'telegram') &&
    typeof row.label_bn === 'string' &&
    row.label_bn.trim() !== '' &&
    (row.hint_bn === undefined || typeof row.hint_bn === 'string')
  );
}

/**
 * What the phone-entry screens may OFFER (docs/07 § 3 — "offer it as a choice, not only
 * as a failure path").
 *
 * Never which channel is healthy: the Worker keeps that to itself on purpose, and picks.
 * A row this build does not know — a third channel, a malformed one — is dropped rather
 * than drawn, so the screen can only ever offer a transport `/otp/request` accepts.
 */
export async function fetchChannels(signal?: AbortSignal): Promise<ChannelOption[]> {
  const body = await api.get<{ options?: unknown }>('/v1/auth/channels', {
    anonymous: true,
    signal,
  });
  const rows = Array.isArray(body?.options) ? (body.options as unknown[]) : [];
  return rows.filter(isChannelOption).map((row) => ({
    key: row.key,
    label_bn: row.label_bn,
    ...(typeof row.hint_bn === 'string' && row.hint_bn.trim() !== '' ? { hint_bn: row.hint_bn } : {}),
  }));
}

/* ════════════════════════════════════════════ Telegram — door B's first half ══ */

/**
 * The answer to `POST /v1/auth/telegram/start-link`, as the screens need it.
 *
 * The Worker answers the two non-links WITHOUT an error envelope — `503 {available:
 * false}` when no bot is configured and `429 {retry_after_seconds}` past five links an
 * hour — so the transport raises both as `UnmappedApiError`. They are named here, where
 * the route is known, rather than left for every screen to recognise by status.
 */
export type TelegramLinkResult =
  | { outcome: 'link'; deep_link: string; expires_in_seconds: number }
  | { outcome: 'unavailable' }
  | { outcome: 'throttled'; retry_after_seconds: number };

/**
 * Only ever a link to Telegram.
 *
 * The URL is opened in a new tab on a person's say-so. It comes from our own Worker, and
 * it is still checked: a response that pointed anywhere else — a bug, a misconfigured
 * proxy — would otherwise be a link the product opened for the person, which is exactly
 * how an open redirect is built.
 */
export function isTelegramDeepLink(url: string): boolean {
  return /^https:\/\/t\.me\/[A-Za-z0-9_]{3,64}\?start=[A-Za-z0-9_-]{1,64}$/.test(url);
}

/**
 * Ask the Worker for the deep link that binds this person's Telegram chat to the number
 * they typed (`apps/api/src/auth/channels/telegram.ts § door B`).
 *
 * Anonymous: the person has a phone number and nothing else yet. The response is the
 * same whether or not the number is already registered — whether a START may bind is
 * decided at START, by the Worker, and never answered here.
 */
export async function startTelegramLink(phone_e164: string): Promise<TelegramLinkResult> {
  try {
    const body = await api.post<{ available?: unknown; deep_link?: unknown; expires_in_seconds?: unknown }>(
      '/v1/auth/telegram/start-link',
      { phone_e164 },
      { anonymous: true },
    );
    const link = typeof body?.deep_link === 'string' ? body.deep_link : '';
    if (body?.available !== true || !isTelegramDeepLink(link)) {
      // A 200 that is not a Telegram link is not something to open. Named as what it
      // means for the person — this door is not open — and recorded as what it is.
      console.error('[auth] start-link answered 200 without a Telegram deep link', body);
      return { outcome: 'unavailable' };
    }
    const ttl = typeof body.expires_in_seconds === 'number' ? body.expires_in_seconds : 15 * 60;
    return { outcome: 'link', deep_link: link, expires_in_seconds: ttl };
  } catch (cause) {
    if (cause instanceof UnmappedApiError && cause.status === 503) return { outcome: 'unavailable' };
    const wait = retryAfterSeconds(cause);
    if (wait !== null) return { outcome: 'throttled', retry_after_seconds: wait };
    throw cause;
  }
}

/**
 * The seconds a `429 {retry_after_seconds}` asks for, or `null` for anything else.
 *
 * `/otp/request`, `/otp/verify` and `/telegram/start-link` all answer a throttle in this
 * one shape, deliberately without an envelope (see the Worker's comment on it), so the
 * reading of it lives in one place.
 */
export function retryAfterSeconds(cause: unknown): number | null {
  if (!(cause instanceof UnmappedApiError) || cause.status !== 429) return null;
  const payload = cause.payload as { retry_after_seconds?: unknown } | null;
  const seconds = payload?.retry_after_seconds;
  return typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0 ? seconds : 3600;
}

/** The Worker's answer when a code went out. It never says on which channel, on purpose. */
export interface OtpSent {
  outcome: 'sent';
  expires_at: string;
}

/**
 * The Worker's answer when no channel could carry a code and the identity was NEW.
 *
 * docs/07 § 4 — no dead end. A new identity is admitted with a real session and a
 * reduced surface; an EXISTING one is told to wait and gets nothing, which arrives
 * here as a thrown `E_VERIFICATION_PENDING` instead. The two must not be flattened
 * into one another: this shape carries tokens and that one does not, and the
 * asymmetry is the security fix (`apps/api/src/auth/otp.ts § deferIdentity`).
 */
export interface OtpDeferred extends SessionTokens {
  outcome: 'deferred';
  identity_id: string;
  verification_state: 'deferred';
  notice: { code: string; message_bn: string; message_en: string };
}

export type OtpRequestResult = OtpSent | OtpDeferred;

export interface RequestOtpInput {
  /** Already E.164 — call `toE164` first, so the screen decides what to do with `null`. */
  phone_e164: string;
  /** `turnstileToken()`. `null` means no token exists; the field is then omitted. */
  challengeToken: string | null;
  /**
   * The channel the person ASKED for — docs/07 § 3, "offer it as a choice, not only as a
   * failure path". A preference and never an instruction: the Worker picks the transport,
   * falls back to the other when this one cannot carry the code, and never says which one
   * did (`apps/api/src/auth/channels/pool.ts § select`). Absent, the field is omitted and
   * the Worker's own order applies. The founder's sign-in sends `telegram`, because door B′
   * bound a Telegram chat to their number a moment ago.
   */
  channel?: OtpChannel | undefined;
}

/**
 * Ask for a code.
 *
 * On `deferred` the session is adopted here rather than by the caller: the tokens are
 * in this response and nowhere else, so a caller that forgot to store them would have
 * signed the person in and thrown the proof away.
 */
export async function requestOtp(input: RequestOtpInput): Promise<OtpRequestResult> {
  const result = await api.post<OtpRequestResult>(
    '/v1/auth/otp/request',
    {
      phone_e164: input.phone_e164,
      device_id: deviceId(),
      // Omitted, never null: the body is `strictObject` and `.optional()`.
      ...(input.channel === undefined ? {} : { channel: input.channel }),
      ...(input.challengeToken === null ? {} : { turnstile_token: input.challengeToken }),
    },
    { anonymous: true },
  );
  if (result.outcome === 'deferred') adoptSession(result);
  return result;
}

export interface VerifiedSession extends SessionTokens {
  outcome: 'verified';
  identity_id: string;
}

/**
 * Send the code. A verified code is the moment a session exists, so it is adopted
 * before this resolves and `currentAccessToken()` is live for the caller's next line.
 *
 * A wrong or expired code does not come back here at all: the Worker answers
 * `E_VERIFICATION_PENDING` with the sentence it wants shown, which the client throws
 * as an `ApiError` (`apps/api/src/auth/index.ts § /otp/verify`).
 */
export async function verifyOtp(input: { phone_e164: string; code: string }): Promise<VerifiedSession> {
  const result = await api.post<VerifiedSession>(
    '/v1/auth/otp/verify',
    {
      phone_e164: input.phone_e164,
      // The Worker normalises Bangla digits too (`auth/codes.ts`); doing it here as
      // well means the comparison is over the same four characters either way.
      code: toAsciiDigits(input.code).trim(),
      device_id: deviceId(),
    },
    { anonymous: true },
  );
  adoptSession(result);
  return result;
}
